// Factors & Number Theory Calculators helpers: listing factors, generating
// primes, classic number-theory checks (odd/even, perfect, coprime,
// Armstrong, palindrome, triangular), Fibonacci, and divisor sums/counts.
// GCD, LCM, prime factorization, prime checking, factorial, and perfect
// square/cube checking already live in lib/tools/arithmeticCalculators.ts,
// lib/tools/probabilityRandomCalculators.ts, and lib/tools/powersRootsCalculators.ts
// respectively - this file deliberately avoids re-implementing those.
import { t } from './i18n'

function gcdTwo(a: number, b: number): number {
    a = Math.abs(a)
    b = Math.abs(b)
    return b === 0 ? a : gcdTwo(b, a % b)
}

// ============================================================
// 1. Factors Calculator (All Factors of a Number)
// ============================================================
export function factorsCalculator(params: { number: unknown }): { factors_list: string; factor_count: number } {
    const n = Math.max(Math.abs(Math.round(Number(params.number)) || 1), 1)
    const factors: number[] = []
    for (let i = 1; i * i <= n; i++) {
        if (n % i === 0) {
            factors.push(i)
            if (i !== n / i) factors.push(n / i)
        }
    }
    factors.sort((a, b) => a - b)
    return { factors_list: factors.join(', '), factor_count: factors.length }
}

// ============================================================
// 2. Prime Numbers Generator (List Primes up to N)
// ============================================================
export function primeNumbersGenerator(params: { limit: unknown }): { primes_list: string; prime_count: number } {
    const limit = Math.max(2, Math.min(Math.round(Number(params.limit)) || 100, 10000))
    const sieve = new Array(limit + 1).fill(true)
    sieve[0] = sieve[1] = false
    for (let i = 2; i * i <= limit; i++) {
        if (sieve[i]) {
            for (let j = i * i; j <= limit; j += i) sieve[j] = false
        }
    }
    const primes: number[] = []
    for (let i = 2; i <= limit; i++) if (sieve[i]) primes.push(i)
    return { primes_list: primes.join(', '), prime_count: primes.length }
}

// ============================================================
// 3. Odd or Even Number Checker
// ============================================================
const EVEN_LABEL: Record<string, string> = {
    en: 'Even', ru: 'Чётное', lv: 'Pāra', pl: 'Parzysta', es: 'Par', fr: 'Pair', it: 'Pari', de: 'Gerade',
}
const ODD_LABEL: Record<string, string> = {
    en: 'Odd', ru: 'Нечётное', lv: 'Nepāra', pl: 'Nieparzysta', es: 'Impar', fr: 'Impair', it: 'Dispari', de: 'Ungerade',
}
export function oddEvenChecker(params: { number: unknown; language: unknown }): { result: string } {
    const n = Math.round(Number(params.number)) || 0
    const bucket = n % 2 === 0 ? EVEN_LABEL : ODD_LABEL
    return { result: t(bucket, params.language) }
}

// ============================================================
// 4. Perfect Number Checker
// ============================================================
const YES_LABEL: Record<string, string> = { en: 'Yes', ru: 'Да', lv: 'Jā', pl: 'Tak', es: 'Sí', fr: 'Oui', it: 'Sì', de: 'Ja' }
const NO_LABEL: Record<string, string> = { en: 'No', ru: 'Нет', lv: 'Nē', pl: 'Nie', es: 'No', fr: 'Non', it: 'No', de: 'Nein' }
function sumProperDivisors(n: number): number {
    let sum = 0
    for (let i = 1; i < n; i++) if (n % i === 0) sum += i
    return sum
}
export function perfectNumberChecker(params: { number: unknown; language: unknown }): { is_perfect: string; sum_of_proper_divisors: number } {
    const n = Math.max(Math.abs(Math.round(Number(params.number)) || 1), 1)
    const sum = sumProperDivisors(n)
    return { is_perfect: t(sum === n ? YES_LABEL : NO_LABEL, params.language), sum_of_proper_divisors: sum }
}

// ============================================================
// 5. Fibonacci Sequence Calculator
// ============================================================
export function fibonacciSequenceCalculator(params: { term_count: unknown }): { sequence_list: string; nth_value: string } {
    const count = Math.max(1, Math.min(Math.round(Number(params.term_count)) || 10, 1000))
    let a = BigInt(0)
    let b = BigInt(1)
    const seq: bigint[] = [a]
    for (let i = 1; i < count; i++) {
        seq.push(b)
        ;[a, b] = [b, a + b]
    }
    return { sequence_list: seq.map(String).join(', '), nth_value: seq[seq.length - 1].toString() }
}

// ============================================================
// 6. Relatively Prime (Coprime) Checker
// ============================================================
export function coprimeChecker(params: { number1: unknown; number2: unknown; language: unknown }): { is_coprime: string; gcd_value: number } {
    const a = Math.round(Number(params.number1)) || 0
    const b = Math.round(Number(params.number2)) || 0
    const g = gcdTwo(a, b) || 1
    return { is_coprime: t(g === 1 ? YES_LABEL : NO_LABEL, params.language), gcd_value: g }
}

// ============================================================
// 7. Sum of Divisors Calculator
// ============================================================
export function sumOfDivisorsCalculator(params: { number: unknown }): { sum_of_divisors: number; sum_of_proper_divisors: number } {
    const n = Math.max(Math.abs(Math.round(Number(params.number)) || 1), 1)
    let sum = 0
    for (let i = 1; i <= n; i++) if (n % i === 0) sum += i
    return { sum_of_divisors: sum, sum_of_proper_divisors: sum - n }
}

// ============================================================
// 8. Number of Divisors Calculator
// ============================================================
export function numberOfDivisorsCalculator(params: { number: unknown }): { divisor_count: number } {
    const n = Math.max(Math.abs(Math.round(Number(params.number)) || 1), 1)
    let count = 0
    for (let i = 1; i * i <= n; i++) {
        if (n % i === 0) count += i === n / i ? 1 : 2
    }
    return { divisor_count: count }
}

// ============================================================
// 9. Armstrong (Narcissistic) Number Checker
// ============================================================
export function armstrongNumberChecker(params: { number: unknown; language: unknown }): { is_armstrong: string; computed_sum: number } {
    const n = Math.max(Math.abs(Math.round(Number(params.number)) || 0), 0)
    const digits = String(n).split('').map(Number)
    const power = digits.length
    const sum = digits.reduce((s, d) => s + Math.pow(d, power), 0)
    return { is_armstrong: t(sum === n ? YES_LABEL : NO_LABEL, params.language), computed_sum: sum }
}

// ============================================================
// 10. Triangular Number Calculator
// ============================================================
export function triangularNumberCalculator(params: { n: unknown }): { triangular_number: number } {
    const n = Math.max(Math.round(Number(params.n)) || 0, 0)
    return { triangular_number: (n * (n + 1)) / 2 }
}

// ============================================================
// 11. Multiples Calculator (List Multiples of a Number)
// ============================================================
export function multiplesCalculator(params: { number: unknown; count: unknown }): { multiples_list: string; nth_multiple: number } {
    const n = Math.round(Number(params.number)) || 1
    const count = Math.max(1, Math.min(Math.round(Number(params.count)) || 10, 100))
    const list: number[] = []
    for (let i = 1; i <= count; i++) list.push(n * i)
    return { multiples_list: list.join(', '), nth_multiple: list[list.length - 1] }
}

// ============================================================
// 12. Palindromic Number Checker
// ============================================================
export function palindromicNumberChecker(params: { number: unknown; language: unknown }): { is_palindrome: string } {
    const n = Math.abs(Math.round(Number(params.number)) || 0)
    const s = String(n)
    const isPalindrome = s === s.split('').reverse().join('')
    return { is_palindrome: t(isPalindrome ? YES_LABEL : NO_LABEL, params.language) }
}
